import { css } from "@emotion/react";
import React from "react";
import { TypographyVariant, useTheme } from "../theme";

export interface TypographyProps extends Omit<
  React.HTMLAttributes<HTMLElement>,
  "color"
> {
  /**
   * Typographyの実態を特定の要素に変えることが出来ます。
   * variantも指定しない場合のデフォルトは"p"ですが、bodyを指定した場合は"p"、captionまたはbuttonを指定した場合は"span"、それ以外の場合はvariantと同じものがデフォルトになります。
   *
   * @default "p"
   */
  as?:
    | "span"
    | "pre"
    | "p"
    | "h1"
    | "h2"
    | "h3"
    | "h4"
    | "h5"
    | "h6"
    | "label"
    | "div";

  /**
   * 文字の色を決定します。
   *
   * @default "inherit"
   */
  color?:
    | "initial"
    | "inherit"
    | "primary"
    | "secondary"
    | "error"
    | "accent"
    | "textPrimary"
    | "textSecondary"
    | "textDisabled"
    | "textHint";
  /**
   * どのようなタイポグラフィを適用するかを指定します。
   *
   * @default "body"
   */
  variant?: TypographyVariant;
  /**
   * 文字の位置を指定します。
   *
   * @default "inherit"
   */
  align?: "inherit" | "left" | "center" | "right" | "justify";
  /**
   * フォント太くします。
   *
   * @default false
   */
  bolder?: boolean;
  /**
   * 1以上の整数を指定することで、表示される行数を制限します。制限された場合、文末に三点リーダーが付与されます。
   *
   * @default undefined
   */
  clamp?: number;
}

export const getHtmlTag = (variant: TypographyVariant) => {
  switch (variant) {
    case "h1":
    case "h2":
    case "h3":
    case "h4":
    case "h5":
      return variant;
    case "body":
      return "p";
    default:
      return "span";
  }
};

export const Typography = ({
  as,
  variant = "body",
  color,
  align,
  bolder = false,
  clamp,
  ...others
}: TypographyProps) => {
  const theme = useTheme();
  const Component = as ?? getHtmlTag(variant);
  return (
    // eslint-disable-next-line react-hooks/static-components
    <Component
      css={css([
        theme.typography[variant],
        {
          fontWeight: bolder ? "bolder" : theme.typography[variant].fontWeight,
        },
        {
          textAlign: align,
          margin: 0,
        },
        {
          color:
            color === "initial"
              ? "initial"
              : color === "inherit"
                ? "inherit"
                : color === "primary"
                  ? theme.palette.intentions.primary.main
                  : color === "secondary"
                    ? theme.palette.intentions.secondary.main
                    : color === "accent"
                      ? theme.palette.intentions.accent.main
                      : color === "error"
                        ? theme.palette.intentions.error.main
                        : color === "textPrimary"
                          ? theme.palette.text.primary
                          : color === "textSecondary"
                            ? theme.palette.text.secondary
                            : color === "textHint"
                              ? theme.palette.text.hint
                              : color === "textDisabled"
                                ? theme.palette.text.disabled
                                : undefined,
        },
        clamp !== undefined
          ? {
              display: "-webkit-box",
              WebkitLineClamp: clamp,
              lineClamp: clamp,
              overflow: "hidden",
              WebkitBoxOrient: "vertical",
              boxOrient: "vertical",
              whiteSpace: "normal",
            }
          : undefined,
      ])}
      {...others}
    />
  );
};
